import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useSidebar } from '../../contexts/SidebarContext';
import CaseFilters from './components/CaseFilters';
import CaseTable from './components/CaseTable';
import CaseMobileCard from './components/CaseMobileCard';
import BulkActions from './components/BulkActions';
import AssignUserModal from './components/AssignUserModal';
import CreateCaseModal from './components/CreateCaseModal';
import { isOperatorFamilyRole } from '../../lib/operatorRoles';
import { ADMINISTRATIVE_AGENT_ROLE_TYPE } from '../../lib/phaseOperatorAssignmentRoles';
import {
  caseMatchesPhaseFilter,
  getCaseDisplayTimelinePhase,
  getInitialReviewAgentId,
} from '../../lib/caseListPhaseUtils';
import {
  computeCaseWorkflowProgress,
  normalizeCasePhasesForList,
  normalizeCasePhasesList,
} from '../../lib/caseWorkflowProgress';
import { getUnreadCountsByCaseId } from '../../lib/caseUnreadMessages';
import { caseMatchesDentistConceptionFilter } from '../../lib/dentistCaseQueue';
import { getCaseAssistantPath, isAdminAssistantRole } from '../../lib/dentistSubmissionWizard';
import {
  caseMatchesStatusFilter,
  normalizeCaseStatusFilterParam,
} from '../../lib/caseStatusUtils';

function parseCasesListSearchParams(searchParams) {
  const messagesParam = searchParams.get('messages') || '';
  const messages =
    messagesParam === 'with' ||
    messagesParam === 'without' ||
    messagesParam === 'unread'
      ? messagesParam
      : '';

  const conceptionParam = searchParams.get('conception') || '';
  const conception =
    conceptionParam === 'nonSoumise' ||
    conceptionParam === 'aValider' ||
    conceptionParam === 'expediee'
      ? conceptionParam
      : '';

  return {
    status: normalizeCaseStatusFilterParam(searchParams.get('status')),
    phase: searchParams.get('phase') || '',
    urgency: searchParams.get('urgency') || '',
    hasIncidents: searchParams.get('hasIncidents') || '',
    messages,
    conception,
    administrativeAgentId: searchParams.get('administrativeAgentId') || '',
    search: searchParams.get('search') || searchParams.get('q') || '',
    dentistId: searchParams.get('dentistId') || '',
  };
}

const CasesList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [selectedCases, setSelectedCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);

  // ── Assignment modal state ──────────────────────────────────────────
  const [assignModal, setAssignModal] = useState({
    isOpen: false,
    caseId: null,
    type: null,
  });

  const assignModalConfig = {
    prosthetist: {
      title: 'Assigner un prothésiste',
      description: 'Sélectionnez le prothésiste à assigner à cette conception.',
      roleType: 'dentist_supplier',
      field: 'prosthetist_id',
    },
    production_staff: {
      title: 'Assigner un fournisseur de production / distributeur',
      description: 'Sélectionnez le fournisseur de production ou distributeur à assigner à ce cas.',
      roleType: ['production_supplier', 'distributor'],
      field: 'assigned_staff_id',
    },
  };

  const currentModalConfig = assignModal.type
    ? assignModalConfig[assignModal.type]
    : null;

  // ── Fetch current user + role + profile ─────────────────────────────
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      try {
        // Récupérer le rôle
        const { data: roleAssignments, error } = await supabase
          .from('user_role_assignments')
          .select(`
            id,
            is_active,
            role:user_roles (
              role_type,
              role_name
            )
          `)
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching user role for cases list:', error);
        }

        const roleTypes = (roleAssignments || [])
          .map((a) => a?.role?.role_type)
          .filter(Boolean);
        const CASES_LIST_ROLE_PRIORITY = [
          'administrator',
          ADMINISTRATIVE_AGENT_ROLE_TYPE,
          'dentist_client',
          'dentist_supplier',
          ...['production_responsible', 'logistics_coordinator'],
          'operator_3d',
          'operator_thermo',
          'operator_finition',
          'operator_qualite',
        ];
        const resolvedRole =
          CASES_LIST_ROLE_PRIORITY.find((r) => roleTypes.includes(r)) || roleTypes[0] || null;

        // Récupérer le profil avec package et case_number
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, package_id, case_number, current_case_number')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching user profile:', profileError);
        }

        setCurrentUserId(user.id);
        setCurrentUserRole(resolvedRole);
        setUserProfile(profile || null);
      } catch (err) {
        console.error('Unexpected error fetching user profile for cases list:', err);
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAdministrativeAgents = async () => {
      try {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('id')
          .eq('role_type', ADMINISTRATIVE_AGENT_ROLE_TYPE)
          .maybeSingle();

        if (roleError) throw roleError;
        if (!roleData?.id) {
          if (!cancelled) {
            setAdministrativeAgentOptions([
              { value: '', label: 'Tous les agents' },
              { value: '__unassigned__', label: 'Non assigné' },
            ]);
          }
          return;
        }

        const { data: assignments, error: assignError } = await supabase
          .from('user_role_assignments')
          .select('user_id')
          .eq('role_id', roleData.id)
          .eq('is_active', true);

        if (assignError) throw assignError;

        const userIds = [...new Set((assignments || []).map((a) => a.user_id).filter(Boolean))];
        if (userIds.length === 0) {
          if (!cancelled) {
            setAdministrativeAgentOptions([
              { value: '', label: 'Tous les agents' },
              { value: '__unassigned__', label: 'Non assigné' },
            ]);
          }
          return;
        }

        const { data: profiles, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', userIds)
          .eq('is_active', true)
          .order('full_name');

        if (profileError) throw profileError;

        if (!cancelled) {
          setAdministrativeAgentOptions([
            { value: '', label: 'Tous les agents' },
            { value: '__unassigned__', label: 'Non assigné' },
            ...(profiles || []).map((p) => ({
              value: p.id,
              label: p.full_name || p.email || p.id,
            })),
          ]);
        }
      } catch (err) {
        console.error('Error loading administrative agents for filter:', err);
      }
    };

    if (currentUserRole && currentUserRole !== 'dentist_client') {
      loadAdministrativeAgents();
    }

    return () => {
      cancelled = true;
    };
  }, [currentUserRole]);

  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);

  const [filters, setFilters] = useState({
    status: '',
    phase: '',
    urgency: '',
    hasIncidents: '',
    messages: '',
    conception: '',
    administrativeAgentId: '',
    search: '',
    dentistId: '',
  });

  const [administrativeAgentOptions, setAdministrativeAgentOptions] = useState([
    { value: '', label: 'Tous les agents' },
    { value: '__unassigned__', label: 'Non assigné' },
  ]);

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsString);
    const parsed = parseCasesListSearchParams(currentParams);
    const rawStatus = currentParams.get('status') || '';
    const status = currentUserRole === 'dentist_client' ? '' : parsed.status;

    setFilters((current) => ({ ...current, ...parsed, status }));

    if (rawStatus !== status) {
      const normalizedParams = new URLSearchParams(currentParams);
      if (status) normalizedParams.set('status', status);
      else normalizedParams.delete('status');
      setSearchParams(normalizedParams, { replace: true });
    }
  }, [currentUserRole, searchParamsString, setSearchParams]);

// Dans le return, ajoutez le modal


  // ── Fetch cases (reusable) ──────────────────────────────────────────
// Updated fetchCases function with dentist_supplier filtering
// This filters cases to show only those with "Révision Initiale" phase in_progress

const fetchCases = useCallback(async () => {
  if (!currentUserId || !currentUserRole) return;

  setIsLoading(true);
  try {
    let query = supabase
      .from('cases')
      .select(`
        *,
        patient:patients(
          id,
          patient_code,
          name,
          email
        ),
        dentist:user_profiles!cases_user_profile_id_fkey(
          id,
          full_name,
          email
        ),
        assigned_staff:user_profiles!cases_assigned_staff_id_fkey(
          id,
          full_name,
          email
        ),
        phases:case_timeline_phases(
          id,
          phase_name,
          status,
          group,
          phase_order,
          responsible_user_id,
          milestones:phase_milestones(
            milestone_name,
            is_completed
          )
        ),
        incidents:case_incidents(
          id,
          status
        ),
        prosthetist:user_profiles!cases_prosthetist_id_fkey(
          id,
          full_name,
          email
        ),
        package:commercial_packages!cases_package_fkey(
          id,
          package_name,
          package_code
        ),
        case_messages(count)
      `)
      .neq('status', 'archived');

    if (currentUserRole === 'dentist_client') {
      query = query.eq('user_profile_id', currentUserId);
    } else if (currentUserRole === 'dentist_supplier') {
      query = query.eq('prosthetist_id', currentUserId);
    } else if (isOperatorFamilyRole(currentUserRole)) {
      const { data: activePhaseRows, error: phaseQueryError } = await supabase
        .from('case_timeline_phases')
        .select('case_id')
        .eq('responsible_user_id', currentUserId)
        .eq('status', 'in_progress');

      if (phaseQueryError) throw phaseQueryError;

      const phaseCaseIds = [
        ...new Set((activePhaseRows || []).map((row) => row.case_id).filter(Boolean)),
      ];

      if (phaseCaseIds.length > 0) {
        const idList = phaseCaseIds.map((id) => `"${id}"`).join(',');
        query = query.or(`assigned_staff_id.eq.${currentUserId},id.in.(${idList})`);
      } else {
        query = query.eq('assigned_staff_id', currentUserId);
      }
    } else if (
      currentUserRole === 'administrator' ||
      currentUserRole === 'agent_administratif'
    ) {
      // No filter — visibilité globale comme administrateur
    } else {
      setAllCases([]);
      setFilteredCases([]);
      setIsLoading(false);
      return;
    }

    const { data: casesData, error } = await query.order('created_date', {
      ascending: false,
    });

    if (error) throw error;

    // Filter cases for dentist_supplier: only show cases where "Révision Initiale" phase is in_progress
    let filteredCasesData = casesData;
if (isOperatorFamilyRole(currentUserRole)) {
  filteredCasesData = casesData.filter((c) => {
    const phasesList = normalizeCasePhasesList(c.phases);
    const hasActiveWorkshopPhase = phasesList.some(
      (p) => p.status === 'in_progress' && p.responsible_user_id === currentUserId
    );
    if (hasActiveWorkshopPhase || c.assigned_staff_id === currentUserId) {
      const phase1Group1 = phasesList.find(
        (p) => String(p.group) === '1' && p.phase_order === 1
      );
      if (!phase1Group1) return true;
      if (phase1Group1.status === 'in_progress' || phase1Group1.status === 'pending') {
        return false;
      }
      return true;
    }
    return false;
  });
}
    if (currentUserRole === 'dentist_supplier') {
      filteredCasesData = casesData.filter((c) => {
        const revisionPhase = normalizeCasePhasesList(c.phases).find(
          (p) => String(p.group) === '1' &&  p.phase_order === 3
        );
        return revisionPhase && revisionPhase.status === 'in_progress';
      });
    }

    const caseIdsForUnread = filteredCasesData.map((c) => c.id);
    const unreadByCaseId =
      await getUnreadCountsByCaseId(currentUserId, caseIdsForUnread);

    const formattedCases = filteredCasesData.map((c) => {
      const phasesList = normalizeCasePhasesForList(c.phases);
      const currentPhase = getCaseDisplayTimelinePhase(phasesList);
      const workflowProgress = computeCaseWorkflowProgress(
        phasesList,
        c.current_phase || null
      );

      const activeIncidents =
        c.incidents?.filter((i) => i.status !== 'resolved').length || 0;

      const dentistName = c.dentist?.full_name || 'Non assigné';

      const messageCountRaw = c.case_messages?.[0]?.count;
      const messageCount =
        messageCountRaw != null ? Number(messageCountRaw) : 0;

      return {
        id: c.id,
        caseId: c.case_number,
        caseCurrentPhase: c.current_phase || null,
        timelinePhases: phasesList,
        patientId: c.patient?.id,
        patientName: c.patient?.name || 'Patient inconnu',
        patientAvatar:
          'https://ui-avatars.com/api/?name=' +
          encodeURIComponent(c.patient?.name || 'U'),
        patientAvatarAlt: `Avatar de ${c.patient?.name}`,
        phase:
          currentPhase?.phase_name || c.current_phase || 'Non défini',
        phaseRaw: currentPhase?.phase_name || '',
        status: c.status,
        urgency: c.priority,
        incidentCount: activeIncidents,
        messageCount,
        unreadMessageCount: unreadByCaseId[c.id] ?? 0,
        assignedStaff: c.assigned_staff?.full_name || 'Non assigné',
        assignedStaffAvatar:
          'https://ui-avatars.com/api/?name=' +
          encodeURIComponent(c.assigned_staff?.full_name || 'N'),
        assignedStaffAvatarAlt: `Avatar de ${c.assigned_staff?.full_name}`,
        lastActivity: new Date(c.updated_at),
        dentistId: c.dentist?.id || null,
        dentistName,
        prosthetistName: c.prosthetist?.full_name || 'Non assigné',
        packageLabel:
          c.package?.package_name?.trim() ||
          (c.package?.package_code ? String(c.package.package_code) : '') ||
          '—',
        workflowProgress,
        initialReviewAgentId: getInitialReviewAgentId(phasesList),
      };
    });

    setAllCases(formattedCases);
  } catch (error) {
    console.error('Error fetching cases:', error);
  } finally {
    setIsLoading(false);
  }
}, [currentUserId, currentUserRole]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // ── Responsive ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Filtres (état + URL, réapplication quand allCases / filters changent)
  const handleFilterUpdate = (next) => {
    const status =
      currentUserRole === 'dentist_client'
        ? ''
        : normalizeCaseStatusFilterParam(next.status);
    const normalized = { ...next, status };
    setFilters(normalized);

    const nextParams = new URLSearchParams(searchParamsString);
    if (status) nextParams.set('status', status);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (!allCases.length) {
      setFilteredCases([]);
      return;
    }
    let filtered = [...allCases];

    if (
      currentUserRole !== 'dentist_client' &&
      filters?.status &&
      filters.status !== ''
    ) {
      filtered = filtered.filter((c) =>
        caseMatchesStatusFilter(c.status, filters.status)
      );
    }
    if (filters?.phase && filters.phase !== '') {
      filtered = filtered.filter((c) => caseMatchesPhaseFilter(c, filters.phase));
    }
    if (filters?.urgency && filters.urgency !== '') {
      filtered = filtered.filter((c) => c.urgency === filters.urgency);
    }
    if (filters?.hasIncidents === 'yes') {
      filtered = filtered.filter((c) => c.incidentCount > 0);
    } else if (filters?.hasIncidents === 'no') {
      filtered = filtered.filter((c) => c.incidentCount === 0);
    }
    if (filters?.messages === 'with') {
      filtered = filtered.filter((c) => (c.messageCount ?? 0) > 0);
    } else if (filters?.messages === 'without') {
      filtered = filtered.filter((c) => (c.messageCount ?? 0) === 0);
    } else if (filters?.messages === 'unread') {
      filtered = filtered.filter((c) => (c.unreadMessageCount ?? 0) > 0);
    }
    if (filters?.conception && filters.conception !== '') {
      filtered = filtered.filter((c) =>
        caseMatchesDentistConceptionFilter(c, filters.conception)
      );
    }
    if (filters?.administrativeAgentId && filters.administrativeAgentId !== '') {
      if (filters.administrativeAgentId === '__unassigned__') {
        filtered = filtered.filter((c) => !c.initialReviewAgentId);
      } else {
        filtered = filtered.filter(
          (c) => c.initialReviewAgentId === filters.administrativeAgentId,
        );
      }
    }
    if (filters?.dentistId && filters.dentistId !== '') {
      filtered = filtered.filter((c) => c.dentistId === filters.dentistId);
    }
    if (filters?.search && filters.search.trim() !== '') {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(
        (c) =>
          (c.patientName || '').toLowerCase().includes(searchLower) ||
          (c.caseId || '').toLowerCase().includes(searchLower)
      );
    }
    setFilteredCases(filtered);
  }, [allCases, currentUserRole, filters]);

  // ── Selection ───────────────────────────────────────────────────────
  const handleSelectAll = (checked) => {
    setSelectedCases(checked ? filteredCases.map((c) => c.id) : []);
  };

  const handleSelectCase = (caseId, checked) => {
    setSelectedCases((prev) =>
      checked ? [...prev, caseId] : prev.filter((id) => id !== caseId)
    );
  };

  // ── Bulk actions ────────────────────────────────────────────────────
  const handleBulkAction = async (action, value) => {
    if (action === 'clearSelection') {
      setSelectedCases([]);
    } else if (action === 'reassignStaff') {
      try {
        const { error } = await supabase
          .from('cases')
          .update({
            assigned_staff_id: value,
            updated_at: new Date().toISOString(),
          })
          .in('id', selectedCases);
        if (error) throw error;
        setSelectedCases([]);
        await fetchCases();
      } catch (error) {
        console.error('Error reassigning staff:', error);
      }
    } else if (action === 'export') {
      console.log('Exporting cases:', selectedCases);
    }
  };

  // ── Assignment via modal ────────────────────────────────────────────
  const handleAssignProsthetist = (caseId) => {
    setAssignModal({ isOpen: true, caseId, type: 'prosthetist' });
  };

  const handleAssignProductionStaff = (caseId) => {
    setAssignModal({ isOpen: true, caseId, type: 'production_responsible' });
  };

  const handleModalAssign = async (userId) => {
    if (!assignModal.caseId || !currentModalConfig) return;

    const { error } = await supabase
      .from('cases')
      .update({
        [currentModalConfig.field]: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', assignModal.caseId);

    if (error) throw error;

    await fetchCases();
  };

  const handleModalClose = () => {
    setAssignModal({ isOpen: false, caseId: null, type: null });
  };

  // ── Check package and case limit before creating ───────────────────
const handleCreateCase = () => {

  // Ouvrir le modal de création
  setShowCreateCaseModal(true);
};

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Liste des commandes', path: '/cases-list', icon: 'Briefcase' },
  ];

  // ── Loading state ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement des cas...</p>
        </div>
      </div>
    );
  }

  const dentistClientCasesList = currentUserRole === 'dentist_client';

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Liste des commandes
              </h1>
              <p className="text-muted-foreground">
                Gérez et suivez toutes les commandes orthodontiques
              </p>
              {/* Affichage du compteur de cas */}
              
            </div>
             {(dentistClientCasesList ||
              currentUserRole === 'administrator' ||
              currentUserRole === 'agent_administratif') && (
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={handleCreateCase}
            >
              Nouvelle conception
            </Button>
             )}
          </div>

          <CaseFilters
            filters={filters}
            onFilterChange={handleFilterUpdate}
            resultsCount={filteredCases.length}
            hideStatusFilter={dentistClientCasesList}
            showConceptionFilter={dentistClientCasesList}
            showAdministrativeAgentFilter={!dentistClientCasesList}
            administrativeAgentOptions={administrativeAgentOptions}
            unreadMessagesTotal={
              filters.messages === 'unread'
                ? filteredCases.reduce(
                    (sum, c) => sum + (c.unreadMessageCount || 0),
                    0
                  )
                : null
            }
            showDentistFilter={
              currentUserRole && !dentistClientCasesList
            }
            dentistOptions={[
              { value: '', label: 'Tous les dentistes' },
              ...Array.from(
                new Map(
                  allCases
                    .filter((c) => c.dentistId && c.dentistName)
                    .map((c) => [c.dentistId, c.dentistName])
                ).entries()
              ).map(([value, label]) => ({ value, label })),
            ]}
          />

          <BulkActions
            selectedCount={selectedCases.length}
            onAction={handleBulkAction}
          />

          {filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucune commande pour ces filtres</p>
            </div>
          ) : isMobileView ? (
            <div className="space-y-3">
              {filteredCases.map((caseData) => (
                <CaseMobileCard
                  key={caseData.id}
                  caseData={caseData}
                  isSelected={selectedCases.includes(caseData.id)}
                  onSelect={handleSelectCase}
                  isAdmin={currentUserRole === 'administrator'}
                  dentistClientView={dentistClientCasesList}
                  onAssignProsthetist={handleAssignProsthetist}
                  onAssignProductionStaff={handleAssignProductionStaff}
                  viewerRole={currentUserRole}
                />
              ))}
            </div>
          ) : (
            <CaseTable
              cases={filteredCases}
              selectedCases={selectedCases}
              onSelectAll={handleSelectAll}
              onSelectCase={handleSelectCase}
              isAdmin={currentUserRole === 'administrator'}
              dentistClientView={dentistClientCasesList}
              onAssignProsthetist={handleAssignProsthetist}
              onAssignProductionStaff={handleAssignProductionStaff}
              viewerRole={currentUserRole}
            />
          )}
        </div>
      </main>

      {/* Assignment Modal */}
      {currentModalConfig && (
        <AssignUserModal
          isOpen={assignModal.isOpen}
          onClose={handleModalClose}
          onAssign={handleModalAssign}
          roleType={currentModalConfig.roleType}
          title={currentModalConfig.title}
          description={currentModalConfig.description}
        />
      )}

      {/* Package Warning Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-warning/10 mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-warning" />
              </div>
 
              
           

              {userProfile?.package_id && (
                <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cas restants</span>
                    <span className="text-2xl font-bold text-foreground">
                      {userProfile?.current_case_number || 0}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mt-2">
                    <div 
                      className="bg-warning h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(0, ((parseInt(userProfile?.current_case_number) || 0) / (parseInt(userProfile?.case_number) || 1)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {userProfile?.current_case_number || 0} sur {userProfile?.case_number || 0} cas disponibles
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowPackageModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Package"
                  iconPosition="left"
                  onClick={() => {
                    setShowPackageModal(false);
                    navigate('/pack-user');
                  }}
                >
                  Voir les packages
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <CreateCaseModal
        isOpen={showCreateCaseModal}
        onClose={() => setShowCreateCaseModal(false)}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        allowAddPatient={currentUserRole === 'dentist_client'}
        onCaseCreated={(caseData) => {
          if (
            currentUserRole === 'dentist_client' ||
            isAdminAssistantRole(currentUserRole)
          ) {
            navigate(getCaseAssistantPath(caseData.id));
          }
        }}
      />
    </div>
  );
};

export default CasesList;